// Push-notification helpers for danya 🖤 rachel.
//
// Wraps the boilerplate around:
//   - service-worker registration
//   - PushManager.subscribe with VAPID
//   - posting the subscription up to /api/push/subscribe
//   - badge-count clearing
//
// Designed so the UI just calls high-level functions and gets back a
// boolean / promise — no awareness of the low-level Web Push primitives.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const SW_URL = '/sw.js';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const arr = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) arr[i] = rawData.charCodeAt(i);
  return arr;
}

const NOTIF_PROMPT_LS_KEY = 'danya-rachel-notif-prompt-pending';

export function isStandalonePWA() {
  if (typeof window === 'undefined') return false;
  // iOS Safari — launched from home screen.
  if (window.navigator?.standalone === true) return true;
  // Android / desktop installed PWAs.
  const displayModes = ['standalone', 'fullscreen', 'minimal-ui'];
  return displayModes.some((mode) =>
    window.matchMedia?.(`(display-mode: ${mode})`).matches,
  );
}

/** True on localhost / LAN so `npm start` can exercise the pill in Chrome. */
export function isLocalDevHost() {
  if (typeof window === 'undefined') return false;
  const host = window.location?.hostname || '';
  if (host === 'localhost' || host === '127.0.0.1' || host === '[::1]') return true;
  // `npm start` → "On Your Network: http://192.168.x.x:3000"
  if (/^192\.168\.\d{1,3}\.\d{1,3}$/.test(host)) return true;
  if (/^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(host)) return true;
  return false;
}

export function isDevNotificationUI() {
  return process.env.NODE_ENV === 'development' || isLocalDevHost();
}

export function markNotificationPromptPending() {
  try {
    localStorage.setItem(NOTIF_PROMPT_LS_KEY, '1');
  } catch (_e) {
    /* no-op */
  }
}

export function clearNotificationPromptPending() {
  try {
    localStorage.removeItem(NOTIF_PROMPT_LS_KEY);
  } catch (_e) {
    /* no-op */
  }
}

export function isPushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window &&
    (typeof window.isSecureContext === 'undefined' || window.isSecureContext)
  );
}

/**
 * Whether to show the one-time "enable notifications" pill after install.
 * Intentionally does NOT require PushManager — on some VPS / HTTP origins
 * the APIs are absent until the user taps, but the pill must still render
 * inside the installed PWA so the user can opt in.
 */
/** True on dev, installed PWA, or after first PWA launch was recorded. */
export function isNotificationPromptSurface() {
  if (typeof window === 'undefined') return false;
  // `npm start` — show on localhost AND LAN IPs (e.g. 192.168.x.x:3000).
  if (process.env.NODE_ENV === 'development') return true;
  if (isStandalonePWA() || isLocalDevHost()) return true;
  try {
    return localStorage.getItem(NOTIF_PROMPT_LS_KEY) === '1';
  } catch (_e) {
    return false;
  }
}

export function shouldShowNotificationPrompt() {
  if (typeof window === 'undefined') return false;
  if (typeof Notification === 'undefined') return false;
  // Dev / localhost: always show enable/disable while developing.
  if (isDevNotificationUI()) return true;
  if (Notification.permission === 'denied') return false;
  // Production browser + installed PWA: show until the user subscribes
  // (App.js also checks !alreadySubscribed). Do not require standalone
  // PWA — regular Chrome/Safari on the VPS URL must see the pill too.
  return true;
}

export function notificationPermission() {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register(SW_URL, { scope: '/' });
    // Force a network check for an updated SW on every launch. iOS PWAs
    // otherwise only check ~once per day, so a fresh redeploy can stay
    // invisible to the device for a long time. This is cheap (304s usually).
    try {
      await reg.update();
    } catch (_e) {
      /* no-op — network or rate-limit; not fatal */
    }
    return reg;
  } catch (e) {
    console.warn('SW register failed:', e);
    return null;
  }
}

export async function getVapidPublicKey() {
  const res = await fetch(`${BACKEND_URL}/api/push/vapid-public-key`);
  if (!res.ok) throw new Error('vapid-public-key fetch failed');
  const data = await res.json();
  return data.key;
}

/**
 * Subscribe this device for push notifications. Returns true on success.
 *
 * Throws a descriptive Error if anything fails, so the caller can surface
 * a specific toast (e.g. "service worker failed", "subscribe blocked").
 *
 * @param {string} deviceId  - persistent client device ID (from lib/deviceId)
 * @param {object} [opts]
 * @param {boolean} [opts.force=false]
 *   When true (used when the user explicitly taps the "enable notifications"
 *   pill), the function unsubscribes any existing browser PushSubscription
 *   FIRST and then creates a fresh one — guaranteeing the new subscription
 *   is tied to the backend's CURRENT VAPID public key. Without this, an
 *   existing subscription left over from an earlier (broken) backend
 *   keypair will keep returning HTTP 400 / 410 from Apple Push Service
 *   because the cached `applicationServerKey` on the browser side no
 *   longer matches the JWT signature the backend produces.
 */
/** Step 1 — native browser permission sheet (must run inside a user gesture). */
export async function requestNotificationPermission() {
  if (typeof Notification === 'undefined') return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  return Notification.requestPermission();
}

/**
 * Step 2 — after permission is granted: register SW, subscribe, POST to backend.
 * Does NOT call requestPermission — caller must ask first.
 */
export async function activatePushSubscription(deviceId, opts = {}) {
  const { force = false } = opts;
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
    return false;
  }
  if (!isPushSupported()) {
    const needsHttps =
      typeof window !== 'undefined' &&
      window.isSecureContext === false &&
      !isLocalDevHost();
    throw new Error(
      needsHttps
        ? 'push requires HTTPS (install as PWA or use https://)'
        : 'push not supported on this device',
    );
  }

  const reg = await registerServiceWorker();
  if (!reg) {
    throw new Error('service worker registration failed');
  }

  // Wait for SW to be active.
  await navigator.serviceWorker.ready;

  // 3) Subscribe with VAPID.
  const vapidPublicKey = await getVapidPublicKey();
  if (!vapidPublicKey) {
    throw new Error('vapid key not available');
  }

  let subscription = await reg.pushManager.getSubscription();
  if (subscription) {
    if (force) {
      // Always tear down + re-create the subscription on an explicit user
      // request, regardless of whether the cached key looks "correct" — the
      // browser-side comparison is unreliable across browser builds.
      try {
        await subscription.unsubscribe();
      } catch (_e) {
        /* no-op */
      }
      subscription = null;
    } else {
      // Soft path (e.g. silent re-subscribe on launch): only resubscribe
      // when the cached applicationServerKey definitively doesn't match.
      try {
        const existingKey = subscription.options?.applicationServerKey;
        const wantKey = urlBase64ToUint8Array(vapidPublicKey);
        const stale =
          !existingKey ||
          new Uint8Array(existingKey).toString() !== wantKey.toString();
        if (stale) {
          await subscription.unsubscribe();
          subscription = null;
        }
      } catch (_e) {
        /* If comparison fails, keep whatever we have. */
      }
    }
  }
  if (!subscription) {
    subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });
  }

  // 4) Send subscription up to the backend so it can target this device.
  const resp = await fetch(`${BACKEND_URL}/api/push/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      device_id: deviceId,
      subscription: subscription.toJSON(),
      name: '',
    }),
  });
  if (!resp.ok) {
    throw new Error(`backend rejected subscription: HTTP ${resp.status}`);
  }

  return true;
}

/** Ask permission, then auto-activate when the user allows. */
export async function subscribeForPush(deviceId, opts = {}) {
  const perm = await requestNotificationPermission();
  if (perm !== 'granted') return false;
  return activatePushSubscription(deviceId, opts);
}

/** Re-upload browser subscription to backend when permission is already granted. */
export async function syncPushSubscriptionIfGranted(deviceId) {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
    return false;
  }
  if (!isPushSupported()) return false;
  return activatePushSubscription(deviceId, { force: false });
}

/** Poll fallback when Web Push cannot fire (tab open but hidden). */
export function showLocalMessageNotification(count = 1) {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
    return;
  }
  if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
    return;
  }
  const body = count === 1 ? '1 new message' : `${count} new messages`;
  try {
    const n = new Notification('my love', {
      body,
      icon: '/icon-192.png',
      tag: 'danya-rachel-poll-fallback',
    });
    n.onclick = () => {
      window.focus();
      n.close();
    };
  } catch (_e) {
    /* no-op */
  }
}

/**
 * Reset the unread-count badge for this device.
 * Called when the app opens to acknowledge any unread posts.
 */
export async function clearBadge(deviceId) {
  // Clear the local OS-level badge first (instant feedback).
  try {
    if (navigator && 'clearAppBadge' in navigator) {
      await navigator.clearAppBadge();
    }
  } catch (e) {
    /* no-op */
  }
  // Tell the SW to clear too (in case the OS surface differs).
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    reg?.active?.postMessage?.({ type: 'clear-badge' });
  } catch (e) {
    /* no-op */
  }
  // Reset the server-side counter.
  try {
    await fetch(`${BACKEND_URL}/api/push/clear-badge/${encodeURIComponent(deviceId)}`, {
      method: 'POST',
    });
  } catch (e) {
    /* no-op */
  }
}

/**
 * Returns true if this device is already subscribed for push.
 */
export async function isAlreadySubscribed() {
  if (!isPushSupported()) return false;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return false;
    const sub = await reg.pushManager.getSubscription();
    return !!sub && Notification.permission === 'granted';
  } catch (e) {
    return false;
  }
}

/** Tear down browser push subscription + backend record. */
export async function unsubscribeFromPush(deviceId) {
  try {
    const reg = await navigator.serviceWorker?.getRegistration?.();
    const sub = await reg?.pushManager?.getSubscription?.();
    if (sub) await sub.unsubscribe();
  } catch (_e) {
    /* no-op */
  }
  if (deviceId) {
    try {
      await fetch(`${BACKEND_URL}/api/push/unsubscribe/${encodeURIComponent(deviceId)}`, {
        method: 'POST',
      });
    } catch (_e) {
      /* no-op */
    }
  }
}
